import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import type { PublicBooking } from "@/api/types";
import { tx } from "@/components/ui";
import { PublicShell, reasonText } from "@/public/PublicShell";
import { DoneIcon, InfoBox, PBtn, PLink, SummaryCard } from "@/public/pieces";

type Offer = { status: string; slot_start: string; slot_end: string; expires_at: string; tz: string; store: { slug: string; name: Record<string, string>; address: string | null }; service: { id: string; name: Record<string, string> }; manage_token: string | null };
const fmt = (iso: string, tz: string, lang: string) => new Date(iso).toLocaleString(lang, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: tz });

/** /w/:token — one-tap claim of a waitlist offer. First tap wins. */
export function WaitlistClaim() {
  const { token = "" } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const q = useQuery({ queryKey: ["pub-offer", token], queryFn: () => api<Offer>(`/public/waitlist/offers/${token}`), retry: false });
  const claim = useMutation({ mutationFn: () => api<PublicBooking>(`/public/waitlist/offers/${token}/claim`, { method: "POST" }), onSettled: () => q.refetch() });
  if (q.isError) return <PublicShell><InfoBox tone="warn">{t("manage.notFound")}</InfoBox></PublicShell>;
  if (!q.data) return <PublicShell><p className="text-muted">{t("app.loading")}</p></PublicShell>;
  const o = q.data;
  const done = claim.data;
  return (
    <PublicShell title={t("claim.title")}>
      {done && <><DoneIcon /><h2 className="mb-3 text-center text-xl font-semibold">{t("claim.done")}</h2></>}
      {!done && <h2 className="mb-3 text-xl font-semibold">{t("claim.title")}</h2>}
      <SummaryCard rows={[
        { k: t("book.step.service"), v: tx(o.service.name, lang) },
        { k: t("appt.when"), v: fmt(o.slot_start, o.tz, lang) },
        { k: t("nav.store"), v: `${tx(o.store.name, lang)}${o.store.address ? ` · ${o.store.address}` : ""}` },
      ]} />
      {done && (
        <div className="mt-4 grid gap-2">
          <PLink href={`/b/${done.manage_token}`}>{t("claim.manage")}</PLink>
          <PLink href={`/api/v1/public/bookings/${done.manage_token}/ics`}>＋ {t("book.addToCalendar")}</PLink>
        </div>
      )}
      {!done && o.status === "sent" && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-muted">{t("claim.validUntil", { until: fmt(o.expires_at, o.tz, lang) })}</p>
          {claim.isError && <p className="text-[13px] text-danger">{claim.error instanceof ApiError ? reasonText(t, claim.error.detail) : t("app.error")}</p>}
          <PBtn type="button" className="w-full" disabled={claim.isPending} onClick={() => claim.mutate()}>{t("claim.button")}</PBtn>
        </div>
      )}
      {!done && o.status === "claimed" && <div className="mt-4 space-y-3"><InfoBox tone="ok">{t("claim.alreadyYours")}</InfoBox>{o.manage_token && <PLink href={`/b/${o.manage_token}`} className="w-full">{t("claim.manage")}</PLink>}</div>}
      {!done && (o.status === "dead" || o.status === "expired") && <div className="mt-4"><InfoBox tone="warn">{t(o.status === "dead" ? "claim.taken" : "claim.expired")}</InfoBox></div>}
    </PublicShell>
  );
}
